import {isTenantSwitchRequested} from '@lib/tenant-switch';
import {useSession} from 'next-auth/react';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';
import {useEffect, useRef} from 'react';

import {PUBLIC_PATH_PREFIXES} from '../../utils/public-paths';

interface useAuthGuardArgs {
  signIn: string;
}

/**
 * @description Redirects the user to the login page if they are not authenticated and not already on the login page
 */
const useAuthGuard = ({signIn}: useAuthGuardArgs): void => {
  const {data: session, status} = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const hasRedirected = useRef<boolean>(false);

  useEffect(() => {
    const handleRedirect = (url: string): void => {
      if (!hasRedirected.current) {
        router.replace(url);
        hasRedirected.current = true;
      }
    };

    const isPublicPath = PUBLIC_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix));
    if (status === `unauthenticated` && pathname !== signIn && !isPublicPath) {
      const search = searchParams.toString();
      const params = new URLSearchParams(search);
      params.set('callbackUrl', `${pathname}${search ? `?${search}` : ''}`);
      handleRedirect(`/login?${params.toString()}`);
    } else if (status === `authenticated` && pathname === signIn) {
      // Preserve tenant-scoped login links so Auth0 can switch organizations.
      if (!isTenantSwitchRequested(Object.fromEntries(searchParams), session?.tenant)) handleRedirect(`/`);
    }
  }, [status, pathname, router, searchParams, signIn, session]);
};

export default useAuthGuard;
